/**
 * AI 调用记录组件
 * 展示每次 AI 调用的详细信息：模型、Token、成本、延迟、请求/响应数据等
 */

import React, { useState } from 'react'
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Tooltip,
  Button,
  Drawer,
  Descriptions,
  Empty,
  Tabs,
  List,
  Badge,
  Alert,
} from 'antd'
import {
  RobotOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  CodeOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { AICallLog, AICallResponseData } from '@/types/scriptGeneration'

const { Text, Paragraph } = Typography

interface AICallLogsProps {
  logs: AICallLog[]
  loading?: boolean
  compact?: boolean  // 紧凑模式，用于阶段产物标签页
}

// 代码样式
const codeStyle: React.CSSProperties = {
  margin: 0,
  padding: 12,
  background: '#1e1e1e',
  borderRadius: 4,
  fontSize: 12,
  fontFamily: '"Fira Code", "Monaco", "Consolas", monospace',
  color: '#d4d4d4',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: 300,
  overflow: 'auto',
}

/**
 * 格式化 Token 数量
 */
const formatTokens = (tokens: number): string => {
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}k`
  }
  return tokens.toString()
}

/**
 * 提取调用目的阶段名称
 */
const getPhaseLabel = (purpose: string): { phaseNum: number; phaseName: string } => {
  const phaseMatch = purpose.match(/phase(\d+)/)
  if (phaseMatch) {
    const num = parseInt(phaseMatch[1], 10)
    const names: Record<number, string> = {
      1: '文本预处理',
      2: '剧本元信息',
      3: '世界观',
      4: 'NPC对话',
      5: '任务时间线',
      6: '物品变量',
      7: '资源描述',
      8: '验证保存',
    }
    return { phaseNum: num, phaseName: names[num] || purpose }
  }
  return { phaseNum: 0, phaseName: purpose }
}

/**
 * AI 调用记录列表组件
 */
const AICallLogs: React.FC<AICallLogsProps> = ({ logs, loading, compact = false }) => {
  const [selectedLog, setSelectedLog] = useState<AICallLog | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // 计算统计数据
  const stats = React.useMemo(() => {
    if (!logs.length) return null

    const totalCalls = logs.length
    const totalCost = logs.reduce((sum, log) => sum + (log.cost || 0), 0)
    const total_tokens = logs.reduce((sum, log) => sum + (log.tokens?.total || 0), 0)
    const avgLatency = logs.reduce((sum, log) => sum + (log.latency || 0), 0) / totalCalls

    return {
      totalCalls,
      totalCost: totalCost.toFixed(4),
      total_tokens,
      avgLatency: Math.round(avgLatency),
    }
  }, [logs])

  // 表格列定义
  const columns = [
    {
      title: '阶段',
      dataIndex: 'phase',
      key: 'phase',
      width: 100,
      render: (phase: string) => {
        const { phaseNum } = getPhaseLabel(phase)
        return (
          <Tag color={phaseNum > 0 ? 'blue' : 'default'}>
            {phaseNum > 0 ? `Phase ${phaseNum}` : phase}
          </Tag>
        )
      },
    },
    {
      title: '阶段说明',
      dataIndex: 'phase',
      key: 'phaseName',
      width: 100,
      render: (_phase: string) => {
        const { phaseName } = getPhaseLabel(_phase)
        return <Text style={{ fontSize: 12 }}>{phaseName}</Text>
      },
    },
    {
      title: '模型',
      dataIndex: 'model',
      key: 'model',
      width: 120,
      ellipsis: true,
      render: (model: string) => (
        <Tooltip title={model}>
          <Text style={{ fontSize: 12 }}>{model.split('-').slice(-2).join('-')}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Token',
      key: 'tokens',
      width: 100,
      render: (_: unknown, record: AICallLog) => (
        <Tooltip title={`输入: ${record.tokens?.input || 0} / 输出: ${record.tokens?.output || 0}`}>
          <Tag color="cyan">{formatTokens(record.tokens?.total || 0)}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '成本',
      dataIndex: 'cost',
      key: 'cost',
      width: 80,
      render: (cost: number) => (
        <Text type="success" strong>
          ¥{(cost || 0).toFixed(4)}
        </Text>
      ),
    },
    {
      title: '延迟',
      dataIndex: 'latency',
      key: 'latency',
      width: 80,
      render: (latency: number) => {
        const color = latency > 10000 ? 'red' : latency > 5000 ? 'orange' : 'green'
        return <Tag color={color}>{Math.round(latency || 0)}ms</Tag>
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Badge status={status === 'success' ? 'success' : 'error'} text={status} />
      ),
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (timestamp: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(timestamp).toLocaleTimeString('zh-CN', { hour12: false })}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_: unknown, record: AICallLog) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedLog(record)
            setDrawerOpen(true)
          }}
        />
      ),
    },
  ]

  /**
   * 渲染响应内容（如果是格式化内容）
   */
  const renderResponseContent = (response_data?: AICallResponseData) => {
    if (!response_data?.content) {
      return <Empty description="无生成内容" />
    }

    const content = response_data.content

    // 如果是结构化对象，尝试格式化展示
    if (typeof content === 'object' && content !== null) {
      // 检查是否是 NPC 列表
      if ('npcs' in content && Array.isArray((content as Record<string, unknown>).npcs)) {
        const npcs = (content as { npcs: Array<{ id: string; name: string; role: string; description: string }> }).npcs
        return (
          <List
            size="small"
            dataSource={npcs}
            renderItem={(npc) => (
              <List.Item>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="名称">{npc.name}</Descriptions.Item>
                  <Descriptions.Item label="角色">{npc.role}</Descriptions.Item>
                  <Descriptions.Item label="描述">{npc.description}</Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        )
      }

      // 检查是否是任务列表
      if ('quests' in content && Array.isArray((content as Record<string, unknown>).quests)) {
        const quests = (content as { quests: Array<{ id: string; name: string; description: string }> }).quests
        return (
          <List
            size="small"
            dataSource={quests}
            renderItem={(quest) => (
              <List.Item>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="任务ID">{quest.id}</Descriptions.Item>
                  <Descriptions.Item label="名称">{quest.name}</Descriptions.Item>
                  <Descriptions.Item label="描述">{quest.description}</Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        )
      }

      // 检查是否是物品列表
      if ('items' in content && Array.isArray((content as Record<string, unknown>).items)) {
        const items = (content as { items: Array<{ id: string; name: string; type: string }> }).items
        return (
          <List
            size="small"
            dataSource={items}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  <Tag color={item.type === 'key_item' ? 'gold' : 'blue'}>{item.type}</Tag>
                  <Text strong>{item.name}</Text>
                </Space>
              </List.Item>
            )}
          />
        )
      }

      // 检查是否是 Phase 1 文本预处理结果 (包含 title, scenes, summary, dialogues, characters, paragraphs)
      if ('title' in content && ('scenes' in content || 'paragraphs' in content)) {
        const phase1Content = content as {
          title: string
          scenes?: string[]
          summary?: string
          dialogues?: Array<{ speaker: string; content: string; emotion?: string }>
          characters?: string[]
          paragraphs?: string[]
        }
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* 标题 */}
            <Card size="small" title="剧本标题">
              <Text strong style={{ fontSize: 16 }}>{phase1Content.title}</Text>
            </Card>

            {/* 摘要 */}
            {phase1Content.summary && (
              <Card size="small" title="摘要">
                <Paragraph style={{ margin: 0 }}>{phase1Content.summary}</Paragraph>
              </Card>
            )}

            {/* 场景列表 */}
            {phase1Content.scenes && phase1Content.scenes.length > 0 && (
              <Card size="small" title={`场景列表 (${phase1Content.scenes.length})`}>
                <List
                  size="small"
                  dataSource={phase1Content.scenes}
                  renderItem={(scene, idx) => (
                    <List.Item>
                      <Text>{idx + 1}. {scene}</Text>
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {/* 角色列表 */}
            {phase1Content.characters && phase1Content.characters.length > 0 && (
              <Card size="small" title={`角色列表 (${phase1Content.characters.length})`}>
                <Space wrap>
                  {phase1Content.characters.map((char, idx) => (
                    <Tag key={idx} color="blue">{char}</Tag>
                  ))}
                </Space>
              </Card>
            )}

            {/* 对话列表 */}
            {phase1Content.dialogues && phase1Content.dialogues.length > 0 && (
              <Card size="small" title={`对话列表 (${phase1Content.dialogues.length})`}>
                <List
                  size="small"
                  dataSource={phase1Content.dialogues}
                  renderItem={(dialogue) => (
                    <List.Item>
                      <Descriptions column={1} size="small" style={{ width: '100%' }}>
                        <Descriptions.Item label="说话人">
                          <Tag color="green">{dialogue.speaker}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="内容">{dialogue.content}</Descriptions.Item>
                        {dialogue.emotion && (
                          <Descriptions.Item label="情绪">
                            <Tag>{dialogue.emotion}</Tag>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </List.Item>
                  )}
                />
              </Card>
            )}

            {/* 段落列表 */}
            {phase1Content.paragraphs && phase1Content.paragraphs.length > 0 && (
              <Card size="small" title={`段落 (${phase1Content.paragraphs.length})`}>
                <List
                  size="small"
                  dataSource={phase1Content.paragraphs}
                  renderItem={(para, idx) => (
                    <List.Item>
                      <Text style={{ fontSize: 12 }}>{idx + 1}. {para.substring(0, 100)}...</Text>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </Space>
        )
      }

      // 其他结构化内容，尝试以列表形式展示 key 信息
      const entries = Object.entries(content as Record<string, unknown>).slice(0, 10)
      return (
        <Descriptions column={1} size="small" bordered>
          {entries.map(([key, value]) => (
            <Descriptions.Item key={key} label={key}>
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )
    }

    // 简单内容直接显示
    return (
      <pre style={codeStyle}>
        {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
      </pre>
    )
  }

  /**
   * 详情抽屉组件
   */
  const DetailDrawer: React.FC = () => {
    if (!selectedLog) return null

    const { phaseNum, phaseName } = getPhaseLabel(selectedLog.phase)

    return (
      <Drawer
        title={
          <Space>
            <span>AI 调用详情</span>
            <Tag color="blue">Phase {phaseNum}</Tag>
            <Text type="secondary">{phaseName}</Text>
          </Space>
        }
        placement="right"
        width={720}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Tabs
          defaultActiveKey="basic"
          items={[
            {
              key: 'basic',
              label: (
                <span>
                  <InfoCircleOutlined /> 基本信息
                </span>
              ),
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* 基本信息卡片 */}
                  <Card size="small" title="基本信息">
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="调用 ID">{selectedLog.id}</Descriptions.Item>
                      <Descriptions.Item label="模型">
                        <Text copyable>{selectedLog.model}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="调用目的">{selectedLog.phase}</Descriptions.Item>
                      <Descriptions.Item label="状态">
                        <Badge
                          status={selectedLog.status === 'success' ? 'success' : 'error'}
                          text={selectedLog.status}
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="重试次数">{selectedLog.retry_count}</Descriptions.Item>
                      <Descriptions.Item label="调用时间">
                        {new Date(selectedLog.created_at).toLocaleString('zh-CN')}
                      </Descriptions.Item>
                      {selectedLog.errorMessage && (
                        <Descriptions.Item label="错误信息" span={2}>
                          <Alert message={selectedLog.errorMessage} type="error" showIcon />
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>

                  {/* 性能统计 */}
                  <Card size="small" title="Token 使用">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic
                          title="输入 Token"
                          value={selectedLog.tokens?.input || 0}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="输出 Token"
                          value={selectedLog.tokens?.output || 0}
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="总计"
                          value={selectedLog.tokens?.total || 0}
                          valueStyle={{ fontSize: 16, color: '#1890ff' }}
                        />
                      </Col>
                    </Row>
                  </Card>

                  {/* 成本和延迟 */}
                  <Card size="small" title="成本与性能">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic
                          title="成本"
                          value={selectedLog.cost || 0}
                          suffix="元"
                          precision={4}
                          valueStyle={{ fontSize: 16, color: '#52c41a' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="延迟"
                          value={selectedLog.latency || 0}
                          suffix="ms"
                          valueStyle={{
                            fontSize: 16,
                            color:
                              selectedLog.latency > 10000
                                ? '#f5222d'
                                : selectedLog.latency > 5000
                                  ? '#fa8c16'
                                  : '#52c41a',
                          }}
                          prefix={<ClockCircleOutlined />}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="单价估算"
                          value={
                            selectedLog.tokens?.total
                              ? ((selectedLog.cost || 0) / (selectedLog.tokens.total / 1000)).toFixed(4)
                              : 0
                          }
                          suffix="元/1k tokens"
                          valueStyle={{ fontSize: 16 }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Space>
              ),
            },
            {
              key: 'request',
              label: (
                <span>
                  <CodeOutlined /> 请求数据
                </span>
              ),
              children: selectedLog.request_data ? (
                <Card size="small" title="请求参数" styles={{ body: { padding: 0 } }}>
                  <pre style={codeStyle}>
                    {JSON.stringify(selectedLog.request_data, null, 2)}
                  </pre>
                </Card>
              ) : (
                <Empty description="无请求数据" />
              ),
            },
            {
              key: 'response',
              label: (
                <span>
                  <CodeOutlined /> 响应数据
                </span>
              ),
              children: selectedLog.response_data ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* 响应使用统计 */}
                  {selectedLog.response_data.usage && (
                    <Card size="small" title="Token 使用详情">
                      <Row gutter={16}>
                        <Col span={8}>
                          <Statistic
                            title="输入 Token"
                            value={selectedLog.response_data.usage.input_tokens || 0}
                            valueStyle={{ fontSize: 14 }}
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="输出 Token"
                            value={selectedLog.response_data.usage.output_tokens || 0}
                            valueStyle={{ fontSize: 14 }}
                          />
                        </Col>
                        <Col span={8}>
                          <Statistic
                            title="总 Token"
                            value={selectedLog.response_data.usage.total_tokens || 0}
                            valueStyle={{ fontSize: 14, color: '#1890ff' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  )}

                  {/* 响应元信息 */}
                  <Card size="small" title="响应元信息">
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="请求 ID">
                        {selectedLog.response_data.request_id || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="延迟">
                        {selectedLog.response_data.latency_ms || '-'} ms
                      </Descriptions.Item>
                      <Descriptions.Item label="成本">
                        ¥{selectedLog.response_data.cost_yuan?.toFixed(6) || '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>

                  {/* 生成内容 */}
                  <Card size="small" title="AI 生成的核心内容">
                    {renderResponseContent(selectedLog.response_data)}
                  </Card>

                  {/* 原始 JSON */}
                  <Card size="small" title="完整响应 JSON">
                    <pre style={codeStyle}>
                      {JSON.stringify(selectedLog.response_data, null, 2)}
                    </pre>
                  </Card>
                </Space>
              ) : (
                <Empty description="无响应数据" />
              ),
            },
          ]}
        />
      </Drawer>
    )
  }

  return (
    <>
      {/* 紧凑模式：直接展示每个阶段生成的产物内容 */}
      {compact ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {logs.map((log) => {
            const { phaseNum, phaseName } = getPhaseLabel(log.phase || '')
            const content = log.response_data?.content

            return (
              <Card
                key={log.id}
                size="small"
                title={
                  <Space>
                    <Tag color="blue">Phase {phaseNum}</Tag>
                    <Text>{phaseName}</Text>
                  </Space>
                }
                extra={
                  <Space>
                    <Tag color="cyan">{formatTokens(log.tokens?.total || 0)} tokens</Tag>
                    <Text type="secondary">{log.latency}ms</Text>
                    {log.status === 'success' ? (
                      <Tag color="success">成功</Tag>
                    ) : (
                      <Tag color="error">失败</Tag>
                    )}
                  </Space>
                }
              >
                {/* 使用与详情抽屉相同的渲染逻辑 */}
                {content ? (
                  typeof content === 'object' && content !== null ? (
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                      {renderResponseContent({ content } as AICallResponseData)}
                    </div>
                  ) : (
                    <Text>{String(content)}</Text>
                  )
                ) : (
                  <Text type="secondary">无生成内容</Text>
                )}

                {/* 错误信息 */}
                {log.status === 'failed' && log.errorMessage && (
                  <Alert
                    message="调用失败"
                    description={log.errorMessage}
                    type="error"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Card>
            )
          })}
        </Space>
      ) : (
        <>
          {/* 统计卡片 */}
          {stats && (
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="总调用次数"
                    value={stats.totalCalls}
                    prefix={<ThunderboltOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="总成本"
                    value={stats.totalCost}
                    prefix={<DollarOutlined />}
                    suffix="元"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="总 Token 使用"
                    value={stats.total_tokens}
                    prefix={<RobotOutlined />}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="平均延迟"
                    value={stats.avgLatency}
                    prefix={<ClockCircleOutlined />}
                    suffix="ms"
                    valueStyle={{ color: stats.avgLatency > 5000 ? '#f5222d' : '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* 调用记录表格 */}
          <Card title="调用记录明细" size="small">
            <Table
              columns={columns}
              dataSource={logs}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
              size="small"
              scroll={{ x: 900 }}
            />
          </Card>

          {/* 详情抽屉 */}
          <DetailDrawer />
        </>
      )}
    </>
  )
}

export default AICallLogs
